import { Button, NativeSelect, SimpleGrid, TextInput } from "@mantine/core"
import { type FormEvent, useCallback, useEffect, useState } from "react"
import { AuthenticatedAppShell } from "../../components/authenticated-app-shell"
import { Link, navigate } from "../../components/link"
import {
  AppCard,
  EmptyState,
  FormActions,
  PageToolbar,
  SectionHeader,
  StatusBadge,
} from "../../components/ui"
import {
  apiGet,
  apiPost,
  type SiteChannelRollupState,
  type SiteGoal,
  type SiteLandingPageRecord,
  type SiteListSummary,
  type SiteRecord,
} from "../../lib/api"
import { formatLocalDate } from "../../lib/dates"
import { formatDecimal, formatMicrosCurrency } from "../../lib/format"
import { goalLabel } from "../../lib/site-labels"

interface SiteFormState {
  websiteUrl: string
  name: string
  primaryGoal: SiteGoal
}

const emptySiteForm: SiteFormState = {
  websiteUrl: "",
  name: "",
  primaryGoal: "not_sure",
}

export function SitesPage() {
  const [sites, setSites] = useState<SiteRecord[]>([])
  const [siteForm, setSiteForm] = useState<SiteFormState>(emptySiteForm)
  const [savingSite, setSavingSite] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const loadSites = useCallback(async () => {
    setSites(await apiGet<SiteRecord[]>("/api/sites"))
  }, [])

  useEffect(() => {
    let cancelled = false

    void loadSites().catch((reason: unknown) => {
      if (!cancelled) {
        setError(reason instanceof Error ? reason.message : "Failed to load sites.")
      }
    })

    return () => {
      cancelled = true
    }
  }, [loadSites])

  async function createSite(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setSavingSite(true)
    setError(null)
    try {
      const result = await apiPost<{ site: SiteRecord; landingPage: SiteLandingPageRecord }>(
        "/api/sites",
        {
          websiteUrl: siteForm.websiteUrl,
          name: siteForm.name || undefined,
          primaryGoal: siteForm.primaryGoal,
        }
      )
      setSiteForm(emptySiteForm)
      navigate(`/app/sites/${result.site.siteId}`)
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Failed to create site.")
    } finally {
      setSavingSite(false)
    }
  }

  return (
    <AuthenticatedAppShell eyebrow="Sites" title="Sites">
      {() => (
        <>
          <PageToolbar>
            Add each website once. Open a site to manage landing pages, connected accounts, and
            future setup work.
          </PageToolbar>

          {error ? (
            <EmptyState>
              <strong>Site state unavailable</strong>
              <br />
              {error}
            </EmptyState>
          ) : null}

          <AppCard>
            <SectionHeader title="Add site" />
            <form onSubmit={(event) => void createSite(event)}>
              <SimpleGrid cols={{ base: 1, md: 3 }} mt="md">
                <TextInput
                  required
                  label="Website"
                  placeholder="example.com"
                  value={siteForm.websiteUrl}
                  onChange={(event) =>
                    setSiteForm((current) => ({
                      ...current,
                      websiteUrl: event.target.value,
                    }))
                  }
                />
                <TextInput
                  label="Name"
                  placeholder="Defaults to domain"
                  value={siteForm.name}
                  onChange={(event) =>
                    setSiteForm((current) => ({ ...current, name: event.target.value }))
                  }
                />
                <NativeSelect
                  label="Main goal"
                  value={siteForm.primaryGoal}
                  onChange={(event) =>
                    setSiteForm((current) => ({
                      ...current,
                      primaryGoal: event.target.value as SiteGoal,
                    }))
                  }
                  data={[
                    { value: "traffic", label: "Get more visitors" },
                    { value: "leads", label: "Get more signups or leads" },
                    { value: "purchases", label: "Get more purchases" },
                    { value: "not_sure", label: "Not sure yet" },
                  ]}
                />
              </SimpleGrid>
              <FormActions>
                <Button type="submit" disabled={savingSite}>
                  {savingSite ? "Adding..." : "Add site"}
                </Button>
              </FormActions>
            </form>
          </AppCard>

          <AppCard>
            <SectionHeader title="Sites" />
            <div className="site-portfolio-list">
              {sites.map((site) => (
                <SitePortfolioRow key={site.siteId} site={site} />
              ))}
              {sites.length === 0 && !error ? (
                <EmptyState>Add a website to create its home landing page.</EmptyState>
              ) : null}
            </div>
          </AppCard>
        </>
      )}
    </AuthenticatedAppShell>
  )
}

function SitePortfolioRow({ site }: { site: SiteRecord }) {
  const summary = site.summary ?? emptySummary(site.landingPageCount)
  const cpaMicros =
    summary.conversions30Days > 0 ? summary.spendMicros30Days / summary.conversions30Days : null
  const action = siteRecommendedAction(site)

  return (
    <article className="site-portfolio-row">
      <div className="site-portfolio-row__header">
        <div>
          <Link className="table-name-link" href={`/app/sites/${site.siteId}`}>
            {site.name}
          </Link>
          <div className="site-portfolio-row__meta">
            <span>{site.primaryDomain ?? "Domain unset"}</span>
            <span>{goalLabel(site.primaryGoal)}</span>
            <span>Created {formatLocalDate(site.createdAt)}</span>
          </div>
        </div>
        <div className="site-portfolio-row__actions">
          <StatusBadge color={automationModeColor(site.automationMode)}>
            {automationModeLabel(site.automationMode)}
          </StatusBadge>
          <Button component={Link} href={`/app/sites/${site.siteId}`} size="xs">
            Open
          </Button>
        </div>
      </div>

      <div className="site-portfolio-metrics">
        <SiteMetric label="Spend 30d" value={formatMicrosCurrency(summary.spendMicros30Days)} />
        <SiteMetric label="Conv. 30d" value={formatDecimal(summary.conversions30Days)} />
        <SiteMetric
          label="CPA"
          value={cpaMicros === null ? "-" : formatMicrosCurrency(cpaMicros)}
        />
        <SiteMetric label="Live" value={String(summary.liveCampaignCount)} />
        <SiteMetric label="Sync" value={String(summary.needsSyncCount)} />
        <SiteMetric label="Assets" value={String(summary.assetCount)} />
        <SiteMetric label="Pages" value={String(site.landingPageCount)} />
      </div>

      <div className="site-portfolio-row__footer">
        <div className="site-channel-pills">
          <ChannelPill label="Search" state={summary.channels.googleSearch} />
          <ChannelPill label="PMax" state={summary.channels.googlePmax} />
          <ChannelPill label="Meta" state={summary.channels.metaAds} />
        </div>
        <div className="site-portfolio-row__signals">
          <span>{formatStatsPulledAt(summary.latestStatsAt)}</span>
          <span>{summary.segmentCount} segments</span>
          <span>{summary.connectedAdsAccountCount} ad accounts</span>
          <span className={`site-portfolio-row__action site-portfolio-row__action--${action.tone}`}>
            {action.label}
          </span>
        </div>
      </div>
    </article>
  )
}

function SiteMetric({ label, value }: { label: string; value: string }) {
  return (
    <div className="site-portfolio-metric">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  )
}

function ChannelPill({ label, state }: { label: string; state: SiteChannelRollupState }) {
  return (
    <span className={`site-channel-pill site-channel-pill--${state}`}>
      <strong>{label}</strong>
      <span>{channelStateLabel(state)}</span>
    </span>
  )
}

function emptySummary(landingPageCount: number): SiteListSummary {
  return {
    assetCount: landingPageCount,
    segmentCount: 0,
    connectedAdsAccountCount: 0,
    activeCampaignCount: 0,
    liveCampaignCount: 0,
    pausedCampaignCount: 0,
    needsSyncCount: 0,
    failedSyncCount: 0,
    spendMicros30Days: 0,
    conversions30Days: 0,
    clicks30Days: 0,
    latestStatsAt: null,
    channels: {
      googleSearch: "missing",
      googlePmax: "missing",
      metaAds: "missing",
    },
  }
}

function siteRecommendedAction(site: SiteRecord): { label: string; tone: "default" | "warn" } {
  const summary = site.summary
  if (!summary) return { label: "Open setup", tone: "default" }
  if (summary.needsSyncCount > 0) return { label: "Sync changes", tone: "warn" }
  if (summary.failedSyncCount > 0) return { label: "Review failed sync", tone: "warn" }
  if (summary.connectedAdsAccountCount === 0) return { label: "Connect ads", tone: "warn" }
  if (summary.liveCampaignCount === 0) return { label: "Launch campaigns", tone: "default" }
  if (!summary.latestStatsAt) return { label: "Pull stats", tone: "default" }
  if (site.automationMode === "full_auto") return { label: "Running full auto", tone: "default" }
  return { label: "Monitor", tone: "default" }
}

function automationModeLabel(value: SiteRecord["automationMode"]): string {
  if (value === "full_auto") return "Full auto"
  if (value === "assist") return "Assist"
  return "Manual"
}

function automationModeColor(value: SiteRecord["automationMode"]): string {
  if (value === "full_auto") return "green"
  if (value === "assist") return "blue"
  return "gray"
}

function channelStateLabel(state: SiteChannelRollupState): string {
  if (state === "needs_sync") return "Needs sync"
  if (state === "live") return "Live"
  if (state === "paused") return "Paused"
  if (state === "draft") return "Draft"
  if (state === "failed") return "Failed"
  return "Missing"
}

function formatStatsPulledAt(value: string | null): string {
  return value ? `Stats pulled ${formatLocalDate(value)}` : "Stats not pulled"
}
